import type { DateArg } from 'date-fns';
import {
  // eslint-disable-next-line no-restricted-imports
  formatDistanceToNow,
  // eslint-disable-next-line no-restricted-imports
  formatDistanceToNowStrict,
} from 'date-fns';
import type { ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { FormatNumberOptions } from 'react-intl';
import { IntlProvider as ReactIntlProvider, useIntl } from 'react-intl';
import { useHref, useLocation } from 'react-router-dom';

import { Button } from 'atoms/buttons/Button';
import { DEBUG_I18N, ORIGIN } from 'config';
import { useBasenameLocale } from 'hooks/useBasenameLocale';
import type { AvailableLanguage, GenericLanguage } from 'i18n/useTranslations';
import { PREFIXES, useTranslations } from 'i18n/useTranslations';
import { toLanguageCode } from 'lib/i18n';
import { fromLamport } from 'lib/lamport';
import type { RoundingMode } from 'lib/wei';
import { fromWei } from 'lib/wei';

import type { DisplayOptions } from '.';
import { IntlContext, localeConfig } from '.';
import { polyfill } from './polyfill';

interface InnerProps {
  children: ReactNode;
  setLocale: (newLocale: string) => void;
  testMode: boolean;
}

const noop = () => {};

const languages: Record<string, AvailableLanguage> = {
  en: 'en',
  de: 'de',
  es: 'es',
  fr: 'fr',
  it: 'it',
  ru: 'ru',
  tr: 'tr',
  pt: 'pt',
  nl: 'nl',
};

const InnerComponent = ({ children, setLocale, testMode }: InnerProps) => {
  const intl = useIntl();
  const { locale } = intl;
  const config = localeConfig[locale.split('-')[0] as GenericLanguage];
  const [dateLocaleConfig, setDateLocaleConfig] = useState();

  const formatEth = (
    value: number | string,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => {
    const opts = { minimumFractionDigits: 3, ...options };
    return `${intl.formatNumber(Number.parseFloat(value.toString()), opts)}${
      displayOptions?.hideSymbol ? '' : '\xa0ETH'
    }`;
  };

  const formatSol = (
    value: number | string,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => {
    const opts = { minimumFractionDigits: 3, ...options };
    return `${intl.formatNumber(Number.parseFloat(value.toString()), opts)}${
      displayOptions?.hideSymbol ? '' : '\xa0SOL'
    }`;
  };

  const formatCardBonus = (value: number) =>
    intl.formatNumber(value, {
      style: 'percent',
      maximumFractionDigits: 1,
      signDisplay: 'always',
    });

  const formatWei = (
    value: string | bigint,
    roundingMode?: RoundingMode,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => {
    // We display a precision of 4 digits OR at least 2 significant numbers
    // 1Eth is 10^18wei, the length of the string is the power of 10
    const precision = Math.max(4, 18 - value.toString().length + 2);

    const ethValue = fromWei(value, precision, roundingMode);

    // We want to display the significant digits, discarding trailing 0
    const [, fractionnal] = ethValue.toString().split('.');
    return formatEth(
      ethValue,
      {
        minimumFractionDigits: fractionnal?.length,
        ...options,
      },
      displayOptions
    );
  };

  const formatLamport = (
    value: string | bigint,
    roundingMode?: RoundingMode,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => {
    // We display a precision of 3 digits OR at least 2 significant numbers
    // 1Eth is 10^9lamport, the length of the string is the power of 10
    const precision = Math.max(3, 9 - value.toString().length + 2);

    const solValue = fromLamport(value, precision, roundingMode);

    // We want to display the significant digits, discarding trailing 0
    const [, fractionnal] = solValue.toString().split('.');
    return formatSol(
      solValue,
      {
        minimumFractionDigits: fractionnal?.length,
        ...options,
      },
      displayOptions
    );
  };

  const formatDistanceToNowWithLocale = (
    date: DateArg<Date>,
    { addSuffix } = { addSuffix: true }
  ) =>
    formatDistanceToNow(date, {
      addSuffix,
      locale: dateLocaleConfig,
    });

  const formatDistanceToNowStrictWithLocale = (
    date: DateArg<Date>,
    { addSuffix } = { addSuffix: true }
  ) =>
    formatDistanceToNowStrict(date, {
      addSuffix,
      locale: dateLocaleConfig,
    });

  useEffect(() => {
    if (!testMode && config) {
      config.date().then(c => setDateLocaleConfig(c.default));

      const root = document.querySelector('html');
      if (root) root.dir = config.dir;
    }
  }, [config, testMode]);

  useEffect(() => {
    const html = window.document.documentElement;

    if (html) html.lang = locale.toString();
  }, [locale, config]);

  const language = languages[toLanguageCode(locale)] || 'en';

  return (
    <IntlContext.Provider
      value={{
        ...intl,
        dir: config?.dir || 'ltr',
        language,
        genericLanguage: language.split('-')[0] as GenericLanguage,
        formatEth,
        formatSol,
        formatWei,
        formatLamport,
        formatCardBonus,
        setLocale,
        formatDistanceToNow: formatDistanceToNowWithLocale,
        formatDistanceToNowStrict: formatDistanceToNowStrictWithLocale,
      }}
    >
      {children}
    </IntlContext.Provider>
  );
};

interface Props {
  children: ReactNode;
  testMode?: boolean;
  timeZone?: string;
  locale: ReturnType<typeof useBasenameLocale>['locale'];
  setLocale: (newLocale: string) => void;
}

export const HrefLang = () => {
  const { pathname, search, hash } = useLocation();

  const canonical = useHref({ pathname });

  return (
    <Helmet>
      {Object.entries(PREFIXES).map(([key, value]) => (
        <link
          key={key}
          hrefLang={toLanguageCode(value)}
          href={`${ORIGIN}${key}${pathname}${search}${hash}`}
          rel="alternate"
        />
      ))}
      <link
        hrefLang="x-default"
        href={`${ORIGIN}${pathname}${search}${hash}`}
        rel="alternate"
      />
      <link href={`${ORIGIN}${canonical}`} rel="canonical" />
    </Helmet>
  );
};

export const IntlProvider = memo(function IntlProvider({
  children,
  testMode = false,
  timeZone,
  locale,
  setLocale,
}: Props) {
  const { translations } = useTranslations(locale, testMode);

  useEffect(() => {
    if (testMode) {
      return;
    }
    polyfill(locale.split('-')[0]); // We still need this polyfill to support formatRange even though we don't use it in the codebase.
  }, [locale, testMode]);

  return (
    <ReactIntlProvider
      locale={locale}
      defaultLocale="en"
      key={locale}
      messages={translations}
      onError={noop}
      timeZone={timeZone}
    >
      <InnerComponent setLocale={setLocale} testMode={testMode}>
        {children}
        {!!DEBUG_I18N && (
          <Button
            size="small"
            color="transparent"
            style={{ position: 'fixed', bottom: 20, right: 20 }}
            onClick={async () =>
              navigator.clipboard.writeText(
                document.body.innerText
                  .match(/\[(.+?)\]/g)
                  ?.map(s => s.slice(1, -1))
                  .join('\n') || ''
              )
            }
          >
            Copy i18n keys
          </Button>
        )}
      </InnerComponent>
    </ReactIntlProvider>
  );
});

export const TestIntlProvider = ({ children }: Pick<Props, 'children'>) => {
  const { locale, setLocale } = useBasenameLocale();
  return (
    <IntlProvider testMode locale={locale} setLocale={setLocale} timeZone="UTC">
      {children}
    </IntlProvider>
  );
};
